import React from "react";
import { Button, Flex, Result, Typography } from "antd";
import { ReactComponent as VectechTextIconSvg } from "../../assets/icons/Vectech Text - Main v2.svg";

const { Paragraph, Text } = Typography;

type Props = {
  /**
   * "no-org": signed in, but not a member of any organization in Scout.
   * "denied": Auth0 itself refused the sign-in.
   * "sign-in-failed": the sign-in could not be completed.
   */
  reason: "no-org" | "denied" | "sign-in-failed";
  email?: string;
  /** What Auth0 said, when it said anything. */
  detail?: string;
  onRetry: () => void;
  onLogout: () => void;
  retrying?: boolean;
};

const COPY = {
  "no-org": {
    title: "Not yet authorized",
    body: "has not been added to an organization in Scout yet. Ask your administrator for access, then check again.",
    retry: "Check again",
  },
  denied: {
    title: "Not yet authorized",
    body: "does not have access to Scout yet. Ask your administrator for access, then try again.",
    retry: "Try again",
  },
  "sign-in-failed": {
    title: "We couldn't sign you in",
    body: "could not be signed in to Scout. Try again, or log out and sign in with a different account.",
    retry: "Try again",
  },
};

/**
 * Full-page answer for a user Scout will not serve.
 *
 * Rendered by AuthLayout in place of the whole app - no Navbar, no queries -
 * because everything behind it would only be refused. Log out is always offered:
 * it is the one way to sign in as someone else, and for a login Auth0 refused it
 * also ends the Auth0 session that would otherwise refuse it again.
 */
const NotAuthorized: React.FC<Props> = ({
  reason,
  email,
  detail,
  onRetry,
  onLogout,
  retrying = false,
}) => {
  const copy = COPY[reason];

  return (
    <Flex vertical className="min-h-screen bg-white">
      <div className="px-6 pt-6 md:px-11">
        <VectechTextIconSvg />
      </div>
      <Flex flex={1} align="center" justify="center" className="px-6 pb-24">
        <Result
          status="403"
          title={copy.title}
          subTitle={
            <div className="max-w-md mx-auto">
              <Paragraph className="text-base">
                {email ? (
                  <>
                    Your account <Text strong>{email}</Text> {copy.body}
                  </>
                ) : (
                  <>Your account {copy.body}</>
                )}
              </Paragraph>
              {detail && (
                <Paragraph type="secondary" className="text-sm">
                  {detail}
                </Paragraph>
              )}
            </div>
          }
          extra={
            <Flex gap={12} justify="center">
              <Button type="primary" loading={retrying} onClick={onRetry}>
                {copy.retry}
              </Button>
              <Button onClick={onLogout}>Log out</Button>
            </Flex>
          }
        />
      </Flex>
    </Flex>
  );
};

export default NotAuthorized;
